<!-- eslint-disable vue/html-indent -->
<template>
  <div class="product-groups">
    <p class="product-groups__title">
      {{ $t('Product catalog') }}
    </p>

    <ul v-if="!displayGroups.length" class="product-groups__list">
      <ProductGroupCardSkeleton v-for="i in 7" :key="i" />
    </ul>

    <transition-group name="reveal" tag="ul" class="product-groups__list">
      <li v-for="group in displayGroups" :key="group.id" class="product-group-item product-groups__list-item">
        <router-link
          :to="{ name: 'productsByCategory', params: { productCategory: group.routeName } }"
          class="product-group-item__name"
        >
          {{ group.name }}
        </router-link>

        <div class="product-group-item__content">
          <div class="product-group-item__products">
            <transition-group
              name="reveal"
              tag="ul"
              :class="{
                'product-group-item__products-list': true,
                'product-group-item__products-list--expanded': group.isShowAll,
              }"
            >
              <li
                v-for="product in group.products.slice(0, group.isShowAll ? undefined : defaultProductsCount)"
                :key="product.id"
                class="product-group-item__product"
              >
                <router-link
                  :to="{
                    name: 'productsByProduct',
                    params: { productCategory: group.routeName, productName: product.routeName },
                  }"
                >
                  {{ product.name }}
                </router-link>
              </li>
            </transition-group>

            <button
              v-if="group.products.length > defaultProductsCount"
              type="button"
              class="button-reset product-group-item__more"
              @click="toggleShowAll(group.id)"
            >
              {{ group.isShowAll ? $t('Hide') : $t('{0} More', [group.products.length - defaultProductsCount]) }}
            </button>
          </div>

          <div class="product-group-item__image">
            <img :src="resolveAssetUrl(group.image)" :alt="group.name" />
          </div>
        </div>
      </li>
    </transition-group>

    <button
      v-if="allGroups.length > defaultGroupsCount"
      type="button"
      class="button-reset product-groups__show-all"
      @click="showAllGroups = !showAllGroups"
    >
      {{
        showAllGroups
          ? $t('Hide {0}', [$tc('units.categoryInGroupsList', allGroups.length - defaultGroupsCount)])
          : $t('Show another {0}', [$tc('units.categoryInGroupsList', allGroups.length - displayGroups.length)])
      }}
    </button>
  </div>
</template>

<script>
import { resolveAssetUrl } from '@/helpers/url'
import { mapGetters, mapState } from 'vuex'
import ProductGroupCardSkeleton from '../skeletons/ProductGroupCardSkeleton.vue'

export default {
  name: 'ProductGroups',
  components: {
    ProductGroupCardSkeleton,
  },
  data: () => ({
    allGroups: [],
    showAllGroups: false,
  }),
  computed: {
    ...mapState('product', ['groups']),
    ...mapGetters('product', ['productsByGroup']),
    ...mapState('profileProduct', ['availableFilters']),
    defaultProductsCount() {
      return 3
    },
    defaultGroupsCount() {
      return 10
    },
    displayGroups() {
      return this.allGroups.slice(0, this.showAllGroups ? undefined : this.defaultGroupsCount)
    },
  },
  async created() {
    await this.$store.dispatch('product/listProducts', {
      params: { page_size: 1000 },
    })
    await this.$store.dispatch('product/listProductGroups')
    await this.$store.dispatch('profileProduct/getFilterDataForAllProducts')

    const availableGroupsSet = new Set((this.availableFilters.groups || []).map(g => g.id))
    const availableProductsSet = new Set((this.availableFilters.products || []).map(p => p.id))

    this.allGroups = this.groups
      .filter(g => availableGroupsSet.has(g.id))
      .map(group => ({
        ...group,
        products: this.productsByGroup(group.id).filter(p => availableProductsSet.has(p.id)),
        isShowAll: false,
      }))
  },
  methods: {
    resolveAssetUrl,
    toggleShowAll(groupId) {
      const item = this.allGroups.find(item => item.id === groupId)
      item.isShowAll = !item.isShowAll
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/common-imports.scss';
@import '@/assets/scss/silence/scroll-bar-decorative';
@import '@/assets/scss/transitions.scss';

.product-groups {
  &__title {
    font-size: 20px;
    margin-bottom: 24px;
    font-weight: 600;

    @include media-breakpoint-up(sm) {
      font-size: 24px;
    }

    > span {
      color: $GreenM;
    }
  }

  &__list {
    display: grid;
    gap: 8px;
    grid-template-columns: repeat(2, 1fr);

    @include media-breakpoint-up(sm) {
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    }
  }

  &__show-all {
    padding: 12px;
    font-weight: 500;
    background-color: $White;
    border-radius: 16px;
    margin-top: 16px;
    width: 100%;
    font-weight: 400;
    border: 1px solid $LGray3;
    transition: border-color 200ms ease;

    &:hover {
      border-color: $LGray6;
    }
  }
}

.product-group-item {
  background-color: $White;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;

  @include media-breakpoint-down(xs) {
    padding: 12px;
    border-radius: 12px;
  }

  &__name {
    font-weight: 500;
    margin-bottom: 8px;
    color: inherit;

    transition: color 200ms ease;

    &:hover {
      color: $GreenM;
      text-decoration: none;
    }
  }

  &__content {
    display: grid;
    grid-template-columns: 1fr 40px;
    column-gap: 8px;
    flex: 1;

    @include media-breakpoint-up(sm) {
      grid-template-columns: 1fr 50px;
      column-gap: 16px;
    }
  }

  &__products {
    grid-column: 1/2;
    font-size: 14px;
    line-height: 1.2;
    display: flex;
    flex-direction: column;
    min-width: 80px;
    min-height: 85px;

    @include media-breakpoint-up(sm) {
      min-width: 100px;
    }

    @media screen and (max-width: 330px) {
      min-width: 70px;
    }
  }

  &__products-list {
    flex: 1;
    max-height: 90px;
    transition: max-height 300ms ease;
  }

  &__products-list--expanded {
    max-height: 220px;
    overflow-y: auto;
    @extend %scroll-bar-decorative-small;
  }

  &__product {
    cursor: pointer;

    > a {
      display: block;
      line-height: 1.25;
      color: inherit;
      transition: color 200ms ease;

      &:hover {
        color: $GreenM;
        text-decoration: none;
      }
    }

    &:not(:last-child) {
      padding-bottom: 4px;
    }
  }

  &__image {
    grid-column: 2/3;
    height: 100%;
    display: flex;
    align-items: flex-end;

    > img {
      display: block;
      object-fit: contain;
      width: 40px;
      height: 40px;

      @include media-breakpoint-up(sm) {
        width: 50px;
        height: 50px;
      }
    }
  }

  &__more {
    text-align: left;
    color: $GreenM;
    padding-top: 8px;
    margin-top: auto;
  }
}
</style>
